/**
 * THE LESSON TEXT IS MARKDOWN, AND WAS BEING SHOWN AS SOURCE.
 *
 * Every screen's `learning_point` is authored in markdown -- 1,423 bold spans across 141
 * screens in 11 chapters, plus 121 blockquotes, 137 italics, 122 bullets, 32 numbered items,
 * 20 table rows and 3 code spans, measured 2026-08-31. The renderer put each paragraph in a
 * text node, so a student read literal asterisks:
 *
 *     **Your job is to notice what they cannot.**
 *
 * on nearly every screen of the course.
 *
 * NO HTML IS EVER PARSED OR INJECTED. This returns React elements, not markup, and there is no
 * `dangerouslySetInnerHTML` anywhere in it. That is the whole security argument: React escapes
 * every text node it renders, so content cannot introduce an element however it is written.
 * A markdown library plus a sanitiser would be two dependencies and a bigger surface to reason
 * about; this handles exactly the constructs the content actually contains, and anything it
 * does not recognise falls through as ordinary text rather than disappearing.
 *
 * The content is authored by Henry-Griffitts, not by students -- but "our own content is
 * trusted" is the assumption that ages badly, so the safety here does not depend on it.
 */

// Bold BEFORE italic: `**x**` must not be seen as an empty italic wrapping `*x*`.
// Code first so a tick span is never re-scanned for emphasis.
//
// THE ITALIC RULE IS DELIBERATELY STRICT, and the test that forced it is worth keeping in mind:
// a naive /\*[^*\n]+\*/ turns "2 ** 3 is not bold, and a lone * stays" into
// "2 * 3 is not bold, and a lone  stays" — it pairs one asterisk from the `**` with the lone
// one and DELETES the text between them. Silently losing a sentence is far worse than showing
// an asterisk, so the delimiters follow CommonMark's flanking idea: an opener may not be
// preceded by a word character or another asterisk and may not be followed by whitespace; a
// closer may not be preceded by whitespace nor followed by a word character or asterisk.
// Anything that fails those tests stays as literal text.
const INLINE =
  /(`[^`\n]+`|\*\*[^*\n]+\*\*|(?<![\w*])\*(?!\s)[^*\n]+?(?<!\s)\*(?![\w*]))/g;

/** One line of markdown -> React nodes. Unrecognised syntax survives as text. */
export function renderInline(text, keyPrefix = "i") {
  const src = String(text == null ? "" : text);
  const out = [];
  let last = 0;
  let m;
  INLINE.lastIndex = 0;
  let n = 0;
  while ((m = INLINE.exec(src)) !== null) {
    if (m.index > last) out.push(src.slice(last, m.index));
    const tok = m[0];
    const key = `${keyPrefix}-${n++}`;
    if (tok.startsWith("**")) {
      out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("`")) {
      out.push(<code key={key}>{tok.slice(1, -1)}</code>);
    } else {
      out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < src.length) out.push(src.slice(last));
  return out;
}

/** Preserve author line breaks inside a block -- several screens lay out a list of terms
 *  one per line without using bullet syntax, and joining them into a wall is a real loss. */
function withBreaks(lines, keyPrefix) {
  const out = [];
  lines.forEach((line, i) => {
    if (i > 0) out.push(<br key={`${keyPrefix}-br-${i}`} />);
    out.push(...renderInline(line, `${keyPrefix}-${i}`));
  });
  return out;
}

const isQuote = (l) => /^>\s?/.test(l);
const isBullet = (l) => /^[-*]\s+/.test(l);
const isNumber = (l) => /^\d+\.\s+/.test(l);
const isTableRow = (l) => /^\|/.test(l);
const isTableRule = (l) => /^\|[\s:|-]+\|?\s*$/.test(l);

// A PLAIN FUNCTION, NOT A COMPONENT. As `<Table/>` the real th/td elements only come into
// existence when React renders it, so the returned tree holds an opaque component node — which
// means neither a caller nor a test can inspect what was actually built. Returning the elements
// directly keeps the whole block introspectable, and the table test is what caught it.
function buildTable(lines, k) {
  const rows = lines.filter((l) => !isTableRule(l)).map((l) =>
    l.replace(/^\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim())
  );
  if (!rows.length) return null;
  const [head, ...body] = rows;
  return (
      <table className="edu-table">
        <thead>
          <tr>{head.map((c, i) => <th key={`${k}-h-${i}`}>{renderInline(c, `${k}-h-${i}`)}</th>)}</tr>
        </thead>
        <tbody>
          {body.map((r, ri) => (
            <tr key={`${k}-r-${ri}`}>
              {r.map((c, ci) => <td key={`${k}-r-${ri}-${ci}`}>{renderInline(c, `${k}-${ri}-${ci}`)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
  );
}

/**
 * A markdown block -> React elements. Blocks are separated by blank lines, which is how the
 * content is authored.
 */
export function renderBlocks(text, { pClass = "edu-lesson-p" } = {}) {
  const body = String(text == null ? "" : text).trim();
  if (!body) return [];
  const blocks = body.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);

  // GROUPED BY RUNS OF LINES, NOT BY WHOLE BLOCKS.
  //
  // This originally asked `lines.every(isBullet)` and fell back to a paragraph otherwise, which
  // is wrong for how the course is actually written: a lead-in sentence followed by its bullets
  // sits in ONE block, so `every` was false and the whole thing rendered as a paragraph with the
  // "- " markers visible. Caught on ch13 L2[1] and ch14 L3[0] by the real-content fixture — the
  // synthetic tests all passed, because they each used a block of a single pure type.
  const out = [];
  blocks.forEach((block, bi) => {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    let i = 0;
    let run = 0;
    while (i < lines.length) {
      const k = `b${bi}-${run++}`;
      const kind = isTableRow(lines[i]) ? "table"
        : isQuote(lines[i]) ? "quote"
        : isBullet(lines[i]) ? "bullet"
        : isNumber(lines[i]) ? "number"
        : "plain";
      const same = (l) => (
        kind === "table" ? isTableRow(l)
          : kind === "quote" ? isQuote(l)
          : kind === "bullet" ? isBullet(l)
          : kind === "number" ? isNumber(l)
          : !isTableRow(l) && !isQuote(l) && !isBullet(l) && !isNumber(l)
      );
      const j0 = i;
      while (i < lines.length && same(lines[i])) i += 1;
      const group = lines.slice(j0, i);

      if (kind === "table") {
        const t = buildTable(group, k);
        if (t) out.push(<div key={k} className="edu-table-wrap">{t}</div>);
      } else if (kind === "quote") {
        out.push(
          <blockquote key={k} className="edu-quote">
            {withBreaks(group.map((l) => l.replace(/^>\s?/, "")), k)}
          </blockquote>
        );
      } else if (kind === "bullet") {
        out.push(
          <ul key={k} className="edu-list">
            {group.map((l, li) => (
              <li key={`${k}-${li}`}>{renderInline(l.replace(/^[-*]\s+/, ""), `${k}-${li}`)}</li>
            ))}
          </ul>
        );
      } else if (kind === "number") {
        out.push(
          <ol key={k} className="edu-list">
            {group.map((l, li) => (
              <li key={`${k}-${li}`}>{renderInline(l.replace(/^\d+\.\s+/, ""), `${k}-${li}`)}</li>
            ))}
          </ol>
        );
      } else {
        out.push(<p key={k} className={pClass}>{withBreaks(group, k)}</p>);
      }
    }
  });
  return out;
}

/** Inline-only rendering, for short single-line fields (stems, options, feedback, titles). */
export function Markdown({ text, className }) {
  return <span className={className}>{renderInline(text)}</span>;
}
